import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Les écrans restent vivants quand on passe de l'un à l'autre.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Le problème
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Au comptoir, on ne fait jamais une chose à la fois. On saisit un contrat de
 * dépôt, un client demande le prix d'un vase, on va voir dans les articles, on
 * revient. Avec un routeur ordinaire, l'écran de dépôt est démonté en partant :
 * le déposant choisi, les six articles déjà saisis, l'étape en cours —
 * tout est perdu. Il faut tout retaper devant le client qui attend.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce qu'on fait à la place
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un écran visité **reste monté** et se contente d'être caché. En revenant, on
 * retrouve exactement l'état qu'on avait laissé, sans que chaque écran ait à
 * sauvegarder quoi que ce soit : c'est React qui garde son état, comme si on
 * n'était jamais parti.
 *
 * Deux conséquences qu'il faut assumer :
 *
 * — **Les données peuvent dater.** Un écran caché pendant qu'on encaisse ne
 *   voit pas la vente passer. D'où `useReveil`, que les écrans concernés
 *   utilisent pour se rafraîchir quand ils redeviennent visibles.
 *
 * — **La mémoire monte** avec le nombre d'écrans ouverts. Ils sont huit, et un
 *   écran caché ne coûte que son état : sur un iPad, c'est sans effet.
 *
 * Les écrans ne sont montés qu'à la **première visite** : ouvrir la caisse le
 * matin ne charge pas le bilan, les statistiques et l'agenda au passage.
 */

export interface Volet {
  /** La racine de section : « /depot », « /articles »… */
  racine: string
  element: ReactNode
  /**
   * Écran qu'on ne garde **pas** en vie une fois quitté.
   *
   * Le bilan et le rapport sont derrière un code gérant qui doit être
   * redemandé à chaque ouverture. Or le laissez-passer vit dans l'état de
   * l'écran : le laisser monté reviendrait à ne plus jamais demander le code.
   * Ces écrans-là sont donc démontés en partant, volontairement.
   */
  volatil?: boolean
  /**
   * Écran qui gère lui-même ses sous-adresses.
   *
   * Les contrats affichent la liste et le contrat choisi dans le même écran :
   * `/contrats/C-2026-0091` n'est pas une page de détail séparée, c'est le
   * même écran avec une sélection. Le volet reste donc visible sur ces
   * adresses au lieu de s'effacer derrière un doublon.
   */
  profond?: boolean
}

/**
 * Cet écran est-il celui qu'on regarde ?
 *
 * À utiliser **avant toute redirection déclenchée par un effet**. Un écran
 * caché reste vivant : ses effets continuent de tourner, et une redirection
 * qui part de là détourne la navigation de l'utilisateur vers un écran qu'il
 * a quitté. C'est arrivé aux contrats, qui sélectionnaient le premier de la
 * liste depuis les coulisses et ramenaient sans cesse l'écran au premier plan.
 */
export function useEstVisible(racine: string): boolean {
  const { pathname } = useLocation()
  return pathname === racine || pathname.startsWith(`${racine}/`)
}

/** La section à laquelle appartient une adresse, « /articles/12 » → « /articles ». */
export function sectionDe(chemin: string, racines: string[]): string | null {
  return racines.find((r) => chemin === r || chemin.startsWith(`${r}/`)) ?? null
}

/**
 * Contexte de réveil : chaque écran connaît sa racine et sait s'il est visible.
 * On passe par un événement plutôt que par un contexte React, pour qu'un écran
 * caché ne soit pas re-rendu à chaque changement d'onglet.
 */
const reveils = new EventTarget()

/**
 * Rappelle une fonction chaque fois que l'écran redevient visible.
 *
 * À utiliser partout où l'affichage peut avoir vieilli en coulisse — une liste
 * d'articles, un solde client, un tableau de bord. Le premier affichage n'est
 * pas concerné : l'écran vient de charger ses données lui-même.
 */
export function useReveil(racine: string, quand: () => void): void {
  const rappel = useRef(quand)
  rappel.current = quand

  useEffect(() => {
    const surReveil = (evenement: Event) => {
      if ((evenement as CustomEvent<string>).detail === racine) rappel.current()
    }
    reveils.addEventListener('reveil', surReveil)
    return () => reveils.removeEventListener('reveil', surReveil)
  }, [racine])
}

export function Volets({
  volets,
  detail,
}: {
  volets: Volet[]
  /** Les pages de détail — fiche client, fiche article — rendues par-dessus. */
  detail: ReactNode
}) {
  const { pathname } = useLocation()
  const racines = volets.map((v) => v.racine)
  const active = sectionDe(pathname, racines)
  // Une page de détail remplace le volet de sa section, mais ne le démonte pas.
  // Sauf pour un écran « profond », qui affiche lui-même ses sous-adresses.
  const profonde = volets.find((v) => v.racine === active)?.profond === true
  const surDetail = active !== null && pathname !== active && !profonde

  const [visitees, setVisitees] = useState<string[]>(() => (active ? [active] : []))
  const precedente = useRef<string | null>(null)

  const volatils = volets.filter((v) => v.volatil).map((v) => v.racine)

  useEffect(() => {
    if (!active) return
    setVisitees((liste) => {
      // On oublie les écrans volatils qu'on vient de quitter : c'est ce qui
      // fait redemander le code gérant au retour.
      const nettoyee = liste.filter((r) => r === active || !volatils.includes(r))
      return nettoyee.includes(active) ? nettoyee : [...nettoyee, active]
    })

    // Réveil : on prévient l'écran qui redevient visible, jamais au premier
    // affichage — il vient de charger ses données de lui-même.
    if (precedente.current !== null && precedente.current !== active && !surDetail) {
      reveils.dispatchEvent(new CustomEvent('reveil', { detail: active }))
    }
    precedente.current = active
  }, [active, surDetail])

  return (
    <>
      {volets
        .filter((v) => visitees.includes(v.racine))
        .map((v) => (
          <div
            key={v.racine}
            className="volet"
            // `hidden` plutôt qu'un démontage : c'est toute la question.
            hidden={v.racine !== active || surDetail}
          >
            {v.element}
          </div>
        ))}
      {surDetail && detail}
    </>
  )
}
